/**
 * useResolvePrincipal — resolves a raw console_user string to an IdP
 * principal via the Lighthouse cross-plane identity resolver.
 *
 * P2 endpoint. The hook returns `confidence: "unknown"` on 404, an empty
 * console_user, or the literal `"loginwindow"` value so callers can
 * render the muted fallback state without throwing.
 */
import { useQuery } from "@tanstack/react-query";
import { lighthouseGet, LighthouseApiError } from "@/services/lighthouseApi";
import { useConnectorEnabled } from "@/hooks/connectors/useConnector";
import type {
  IdPPrincipalDTO,
  ResolvePrincipalResponseDTO,
} from "@/types/lighthouseApi";

const UNRESOLVED: IdPPrincipalDTO = { confidence: "unknown" };

export function useResolvePrincipal(
  consoleUser: string | null | undefined,
  options: { deviceId?: string; enabled?: boolean } = {},
) {
  const connectorOn = useConnectorEnabled("lighthouse");
  const trimmed = (consoleUser ?? "").trim();
  const resolvable =
    trimmed.length > 0 && trimmed.toLowerCase() !== "loginwindow";
  const enabled = connectorOn && resolvable && options.enabled !== false;

  return useQuery({
    enabled,
    queryKey: ["lighthouse", "principals", "resolve", trimmed, options.deviceId ?? ""],
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: 1,
    queryFn: async (): Promise<IdPPrincipalDTO> => {
      try {
        const { data } = await lighthouseGet<ResolvePrincipalResponseDTO>(
          "/api/v1/lighthouse/principals/resolve",
          { params: { consoleUser: trimmed, deviceId: options.deviceId } },
        );
        return data?.principal ?? UNRESOLVED;
      } catch (err) {
        if (err instanceof LighthouseApiError && err.status === 404) {
          return UNRESOLVED;
        }
        throw err;
      }
    },
  });
}
